import React from 'react';
import { Link } from 'react-router-dom';
import { Wordmark } from '../components';

const LegalPage = ({ title, children }) => (
  <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--ink)' }}>
    <header style={{
      position: 'sticky', top: 0, zIndex: 100,
      borderBottom: '1px solid var(--line)',
      background: 'oklch(1 0 0 / 0.85)',
      backdropFilter: 'blur(12px)',
      padding: '0 24px',
    }}>
      <div style={{ maxWidth: 800, margin: '0 auto', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Wordmark size={22} />
        </Link>
        <Link to="/"
          style={{ fontSize: 13, color: 'var(--ink-2)', textDecoration: 'none', fontFamily: 'var(--font-mono)', letterSpacing: '.06em' }}>
          ← Back to home
        </Link>
      </div>
    </header>

    <div style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px 100px' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 12 }}>Legal</div>
      <h1 style={{ fontSize: 'clamp(28px, 4vw, 48px)', marginBottom: 12, letterSpacing: '-0.02em' }}>{title}</h1>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', marginBottom: 48 }}>Last updated: June 2026</p>
      <div style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.8 }}>
        {children}
      </div>
    </div>

    <footer style={{ borderTop: '1px solid var(--line)', padding: '24px', textAlign: 'center', fontSize: 12, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
      © 2026 VentyVIP
    </footer>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink)', marginBottom: 12, marginTop: 0 }}>{title}</h2>
    {children}
  </div>
);

export const Terms = () => (
  <LegalPage title="Terms of Service">
    <Section title="1. Acceptance of Terms">
      <p>By accessing or using VentyVIP at <strong>ventyvip.com</strong> ("the Service"), you agree to be bound by these Terms of Service and our <Link to="/privacy" style={{ color: 'var(--primary)' }}>Privacy Policy</Link>. If you do not agree, please do not use the Service.</p>
    </Section>

    <Section title="2. Eligibility">
      <p>You must be at least 13 years old (or 16 in the EEA) to use the Service. By using VentyVIP, you represent and warrant that you meet this age requirement.</p>
    </Section>

    <Section title="3. Description of Service">
      <p>VentyVIP is a free tool that uses AI (powered by Google Gemini) to generate informational guides about events from publicly available event URLs. The Service also features event listings, a blog, organizer services, and event photography booking. The Service is provided "as is" for informational purposes only.</p>
    </Section>

    <Section title="4. AI-Generated Content Disclaimer">
      <p style={{ fontWeight: 600, color: 'var(--ink)' }}>Important: Event guides are generated by AI and may contain inaccuracies.</p>
      <p style={{ marginTop: 10 }}>VentyVIP does not guarantee the accuracy, completeness, or timeliness of any AI-generated guide. Event details including dates, times, locations, prices, weather, dress code, and contact information may be incorrect or outdated. Always verify critical details directly with the event organizer before attending.</p>
      <p style={{ marginTop: 10 }}>VentyVIP is <strong>not</strong> an event organizer, ticket seller, or venue. We provide informational tools only and are not responsible for any event cancellations, changes, or experiences.</p>
    </Section>

    <Section title="5. Acceptable Use">
      <p>You agree not to:</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li>Use the Service for any unlawful purpose</li>
        <li>Attempt to reverse engineer, scrape, bot, or overload the Service</li>
        <li>Submit URLs containing malicious, illegal, or harmful content</li>
        <li>Misrepresent AI-generated guides as official event communications</li>
        <li>Circumvent or interfere with any security features of the Service</li>
        <li>Use the Service to infringe on the intellectual property rights of others</li>
        <li>Use automated systems or bots to access the Service without our express permission</li>
      </ul>
    </Section>

    <Section title="6. Intellectual Property">
      <p>The VentyVIP name, logo, branding, and site design are owned by VentyVIP. All content on the Site, including blog posts, page designs, and graphics, is protected by copyright.</p>
      <p style={{ marginTop: 10 }}>AI-generated event guides are produced for your personal, non-commercial use only and may not be resold, redistributed, or republished without our written permission.</p>
    </Section>

    <Section title="7. User-Submitted Content">
      <p>By submitting information through our forms (organizer inquiries, photography bookings, consultation requests), you grant VentyVIP a non-exclusive, royalty-free right to use that information to provide the requested services and communicate with you.</p>
    </Section>

    <Section title="8. Advertising & Sponsored Content">
      <p>The Service displays third-party advertisements served by Google AdSense and other advertising networks. Some event listings in our Spotlight section may be paid placements or sponsored content. Sponsored content will be clearly labeled where applicable.</p>
      <p style={{ marginTop: 10 }}>We are not responsible for the products, services, or claims made in any third-party advertisements. Clicking on ads will take you to third-party websites governed by their own terms and privacy policies.</p>
    </Section>

    <Section title="9. Third-Party Links & Services">
      <p>The Service references, links to, and embeds content from third-party websites and services, including event pages, social media platforms, and advertising networks. We are not responsible for the content, accuracy, privacy practices, or availability of third-party websites.</p>
      <p style={{ marginTop: 10 }}>Third-party services used by VentyVIP include but are not limited to:</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li>Google (Gemini API, AdSense, Google Ads, Analytics)</li>
        <li>Meta Platforms (Facebook/Instagram Pixel)</li>
        <li>Vercel (hosting)</li>
        <li>Resend (email delivery)</li>
      </ul>
    </Section>

    <Section title="10. Limitation of Liability">
      <p>To the fullest extent permitted by applicable law, VentyVIP and its owners, operators, employees, and affiliates shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from:</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li>Your use of or inability to use the Service</li>
        <li>Reliance on AI-generated content or event information</li>
        <li>Any errors, inaccuracies, or omissions in event guides</li>
        <li>Any third-party content, products, or services accessed through the Service</li>
        <li>Any unauthorized access to or alteration of your data</li>
      </ul>
    </Section>

    <Section title="11. Indemnification">
      <p>You agree to indemnify, defend, and hold harmless VentyVIP and its owners, operators, and affiliates from and against any claims, damages, losses, liabilities, costs, and expenses (including attorney fees) arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.</p>
    </Section>

    <Section title="12. Termination">
      <p>We reserve the right to suspend or terminate your access to the Service at any time, for any reason, without prior notice or liability. All provisions of these Terms that should reasonably survive termination shall survive, including intellectual property provisions, disclaimers, and limitations of liability.</p>
    </Section>

    <Section title="13. Governing Law & Jurisdiction">
      <p>These Terms shall be governed by and construed in accordance with the laws of the State of New York, United States, without regard to its conflict of law provisions. Any disputes arising from these Terms or the Service shall be resolved exclusively in the courts located in New York, New York.</p>
    </Section>

    <Section title="14. Severability">
      <p>If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.</p>
    </Section>

    <Section title="15. Entire Agreement">
      <p>These Terms, together with our <Link to="/privacy" style={{ color: 'var(--primary)' }}>Privacy Policy</Link> and <Link to="/cookies" style={{ color: 'var(--primary)' }}>Cookie Policy</Link>, constitute the entire agreement between you and VentyVIP regarding the use of the Service.</p>
    </Section>

    <Section title="16. Changes to Terms">
      <p>We reserve the right to update these Terms at any time. When we make material changes, we will update the "Last updated" date at the top of this page. Continued use of the Service after changes constitutes acceptance of the updated Terms.</p>
    </Section>

    <Section title="17. Contact">
      <p>For questions about these Terms, please contact us:</p>
      <p style={{ marginTop: 8 }}><strong>Email:</strong> <a href="mailto:info@VentyVIP.com" style={{ color: 'var(--primary)' }}>info@VentyVIP.com</a></p>
      <p style={{ marginTop: 4 }}><strong>Website:</strong> <a href="https://ventyvip.com" style={{ color: 'var(--primary)' }}>ventyvip.com</a></p>
    </Section>
  </LegalPage>
);
